import { renderToString } from "katex";

/** Normalize model-written math while keeping dollar amounts and code literal. */
const SENTINEL = "\uE000";
const STASH = new RegExp(String.raw`${SENTINEL}M(\d+)${SENTINEL}`, "g");

type StashKind = "inline" | "display";

function isValidMath(tex: string): boolean {
  try {
    renderToString(tex, { throwOnError: true, trust: false, strict: "ignore", output: "mathml" });
    return true;
  } catch {
    return false;
  }
}

function transformProse(text: string): string {
  const store: Array<[StashKind, string]> = [];
  const stash = (kind: StashKind, tex: string): string => {
    const i = store.length;
    // TeX punctuation otherwise adds a gap after thousands separators.
    store.push([kind, tex.replace(/(\d),(?=\d)/g, "$1{,}")]);
    return `${SENTINEL}M${i}${SENTINEL}`;
  };

  let s = text
    .replace(/(?<!\\)\$\$([\s\S]+?)\$\$/g, (_m, tex: string) => stash("display", tex))
    .replace(/\\\[([\s\S]+?)\\\]/g, (_m, tex: string) => stash("display", tex))
    .replace(/\\\(([\s\S]+?)\\\)/g, (_m, tex: string) => stash("inline", tex));

  const dollars = [...s.matchAll(/(?<![\\$])\$(?!\$)/g)];
  let cursor = 0;
  let inline = "";
  for (let i = 0; i + 1 < dollars.length; i++) {
    const opening = dollars[i];
    const closing = dollars[i + 1];
    if (!opening || !closing) continue;
    const start = opening.index;
    const end = closing.index;
    const tex = s.slice(start + 1, end);
    // Whitespace next to a delimiter, a currency suffix, or a following digit
    // distinguishes "$5 for $10" and "$5-$10" from "$x$" and "$x + y$".
    if (
      /[\w}\]]/.test(s[start - 1] ?? "") ||
      /[\d]/.test(s[end + 1] ?? "") ||
      /^\d[\d,.]*(?:[kmbt]|bn|mm)\b/i.test(tex) ||
      !tex ||
      tex.trim() !== tex ||
      /[\r\n]/.test(tex) ||
      !isValidMath(tex)
    )
      continue;
    inline += s.slice(cursor, start) + stash("inline", tex);
    cursor = end + 1;
    i++;
  }
  s = inline + s.slice(cursor);

  // Some model responses omit the opening delimiter on a braced math command.
  // Recover only a complete, valid command with its trailing delimiter intact.
  s = s.replace(
    /(?<![\w\\])\\(?:mathbf|mathrm|mathit|mathsf|mathtt|mathbb|mathcal|frac|sqrt)\{(?:\\.|[^\\$\r\n])+?\}\$(?!\$)/g,
    (match) => {
      const tex = match.slice(0, -1);
      return isValidMath(tex) ? stash("inline", tex) : match;
    }
  );

  s = s.replace(/(?<!\\)\$/g, String.raw`\$`);
  return s.replace(STASH, (match, idx: string) => {
    const entry = store[Number(idx)];
    if (!entry) return match;
    const [kind, tex] = entry;
    if (kind === "display") return `\n\n$$\n${tex.trim()}\n$$\n\n`;
    // remark-math does not honor escaped dollars inside single-dollar math.
    // A two-dollar inline fence safely contains currency such as \$911.
    const delimiter = tex.includes("$") ? "$$" : "$";
    return `${delimiter}${tex}${delimiter}`;
  });
}

const CODE_SPLIT = /(```[\s\S]*?```|~~~[\s\S]*?~~~|``[^`]*``|`[^`\n]+`)/g;

export function normalizeMathDelimiters(text: string): string {
  if (!text || typeof text !== "string") return text;
  if (!text.includes("$") && !text.includes(String.raw`\(`) && !text.includes(String.raw`\[`)) {
    return text;
  }
  return text
    .split(CODE_SPLIT)
    .map((segment, i) => (i % 2 === 0 ? transformProse(segment) : segment))
    .join("");
}

export default normalizeMathDelimiters;
